// src/pages/lead-automation/components/LeadFormModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Loader, RefreshCw, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getShops } from '../../../services/api/shops';
import { getProducts } from '../../../services/api/products';
import { getSheetColumns } from '../../../services/api/leadAutomation';
import styles from './LeadFormModal.module.css';

const CANONICAL_FIELDS = [
  'Customer_Name',
  'Address',
  'City_Name',
  'Phone_1',
  'Phone_2',
  'Customer_Email',
  'Private_Reference',
  'Delivery_Notes',
  'Item_1_Quantity',
];

const EMPTY_FORM = {
  name: '',
  googleSheetId: '',
  sheetTabName: '',
  shopId: '',
  defaultProductId: '',
  defaultVariationId: '',
  defaultQuantity: 1,
  lastRowIndex: 1,
  duplicateCheckDays: 7,
  useCityUnknown: true,
  status: 'active',
  fieldMappings: {},
};

// Extract spreadsheet ID from a full Google Sheets URL if pasted
const extractSheetId = (input) => {
  const match = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : input.trim();
};

const LeadFormModal = ({ mode, leadForm, onClose, onSuccess }) => {
  const { t } = useLocalization();
  const la = (key) => t(`leadAutomation.${key}`);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data for selects
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [variations, setVariations] = useState([]);

  // Sheet columns
  const [sheetColumns, setSheetColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && leadForm) {
      setFormData({
        name: leadForm.name || '',
        googleSheetId: leadForm.googleSheetId || '',
        sheetTabName: leadForm.sheetTabName || '',
        shopId: leadForm.shopId ? String(leadForm.shopId) : '',
        defaultProductId: leadForm.defaultProductId ? String(leadForm.defaultProductId) : '',
        defaultVariationId: leadForm.defaultVariationId ? String(leadForm.defaultVariationId) : '',
        defaultQuantity: leadForm.defaultQuantity || 1,
        lastRowIndex: leadForm.lastRowIndex || 1,
        duplicateCheckDays: leadForm.duplicateCheckDays || 7,
        useCityUnknown: leadForm.useCityUnknown !== false,
        status: leadForm.status || 'active',
        fieldMappings: leadForm.fieldMappings || {},
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [mode, leadForm]);

  // Load shops + products on mount
  useEffect(() => {
    getShops({ limit: 200 })
      .then((data) => setShops(data.shops || data || []))
      .catch(() => {});

    getProducts({ limit: 500, status: 'active' })
      .then((data) => setProducts(data.products || data || []))
      .catch(() => {});
  }, []);

  // Sync variations when product changes
  useEffect(() => {
    if (!formData.defaultProductId) {
      setVariations([]);
      return;
    }
    const product = products.find((p) => String(p.id) === formData.defaultProductId);
    setVariations(product?.variations || product?.ProductVariations || []);
  }, [formData.defaultProductId, products]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleMappingChange = (canonicalField, columnName) => {
    setFormData((prev) => {
      const next = { ...prev };
      if (!columnName) {
        const { [canonicalField]: _, ...rest } = next.fieldMappings;
        next.fieldMappings = rest;
      } else {
        next.fieldMappings = { ...next.fieldMappings, [canonicalField]: [columnName] };
      }
      return next;
    });
  };

  const handleLoadColumns = async () => {
    const sheetId = extractSheetId(formData.googleSheetId);
    const tabName = formData.sheetTabName.trim();
    if (!sheetId || !tabName) {
      setColumnsError(la('enterSheetIdAndTabFirst'));
      return;
    }
    setColumnsLoading(true);
    setColumnsError('');
    try {
      const data = await getSheetColumns({ sheetId, tabName });
      setSheetColumns(data.columns || []);
    } catch (err) {
      setColumnsError(err?.response?.data?.message || la('failedToLoadColumns'));
    } finally {
      setColumnsLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = la('validation.nameRequired');
    if (!formData.googleSheetId.trim()) errs.googleSheetId = la('validation.sheetIdRequired');
    if (!formData.sheetTabName.trim()) errs.sheetTabName = la('validation.tabNameRequired');
    if (!formData.shopId) errs.shopId = la('validation.shopRequired');
    if (!formData.defaultProductId) errs.defaultProductId = la('validation.productRequired');
    if (!formData.defaultQuantity || formData.defaultQuantity < 1)
      errs.defaultQuantity = la('validation.quantityMin');
    if (!formData.duplicateCheckDays || formData.duplicateCheckDays < 1)
      errs.duplicateCheckDays = la('validation.duplicateCheckDaysMin');
    if (formData.duplicateCheckDays > 365)
      errs.duplicateCheckDays = la('validation.duplicateCheckDaysMax');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        googleSheetId: extractSheetId(formData.googleSheetId),
        shopId: Number(formData.shopId),
        defaultProductId: Number(formData.defaultProductId),
        defaultVariationId: formData.defaultVariationId ? Number(formData.defaultVariationId) : null,
        defaultQuantity: Number(formData.defaultQuantity),
        lastRowIndex: Number(formData.lastRowIndex) || 1,
        duplicateCheckDays: Number(formData.duplicateCheckDays),
      };
      await onSuccess(payload);
    } catch (err) {
      // handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMappedColumn = (field) => {
    const arr = formData.fieldMappings[field];
    return Array.isArray(arr) && arr.length ? arr[0] : '';
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {mode === 'edit' ? la('editLeadForm') : la('createLeadForm')}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form className={styles.body} onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className={styles.section}>
            <div className={styles.field}>
              <label className={styles.label}>{la('formName')} *</label>
              <input
                className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={la('formNamePlaceholder')}
              />
              {errors.name && <span className={styles.error}>{errors.name}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{la('googleSheetId')} *</label>
              <input
                className={`${styles.input} ${errors.googleSheetId ? styles.inputError : ''}`}
                value={formData.googleSheetId}
                onChange={(e) => handleChange('googleSheetId', e.target.value)}
                placeholder={la('googleSheetIdPlaceholder')}
              />
              <span className={styles.hint}>{la('googleSheetIdHint')}</span>
              {errors.googleSheetId && <span className={styles.error}>{errors.googleSheetId}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{la('sheetTabName')} *</label>
              <input
                className={`${styles.input} ${errors.sheetTabName ? styles.inputError : ''}`}
                value={formData.sheetTabName}
                onChange={(e) => handleChange('sheetTabName', e.target.value)}
                placeholder={la('sheetTabNamePlaceholder')}
              />
              {errors.sheetTabName && <span className={styles.error}>{errors.sheetTabName}</span>}
            </div>
          </div>

          {/* Order defaults */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{la('orderDefaults')}</div>

            <div className={styles.field}>
              <label className={styles.label}>{la('selectShop')} *</label>
              <select
                className={`${styles.select} ${errors.shopId ? styles.inputError : ''}`}
                value={formData.shopId}
                onChange={(e) => handleChange('shopId', e.target.value)}
              >
                <option value="">{la('selectShop')}</option>
                {shops.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
              {errors.shopId && <span className={styles.error}>{errors.shopId}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{la('selectProduct')} *</label>
              <select
                className={`${styles.select} ${errors.defaultProductId ? styles.inputError : ''}`}
                value={formData.defaultProductId}
                onChange={(e) => {
                  handleChange('defaultProductId', e.target.value);
                  handleChange('defaultVariationId', '');
                }}
              >
                <option value="">{la('selectProduct')}</option>
                {products.map((p) => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
              {errors.defaultProductId && <span className={styles.error}>{errors.defaultProductId}</span>}
            </div>

            {variations.length > 0 && (
              <div className={styles.field}>
                <label className={styles.label}>{la('selectVariation')}</label>
                <select
                  className={styles.select}
                  value={formData.defaultVariationId}
                  onChange={(e) => handleChange('defaultVariationId', e.target.value)}
                >
                  <option value="">{la('selectVariation')}</option>
                  {variations.map((v) => (
                    <option key={v.id} value={String(v.id)}>
                      {v.sku || v.name || `Variation ${v.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>{la('defaultQuantity')}</label>
                <input
                  type="number"
                  min="1"
                  className={`${styles.input} ${errors.defaultQuantity ? styles.inputError : ''}`}
                  value={formData.defaultQuantity}
                  onChange={(e) => handleChange('defaultQuantity', e.target.value)}
                />
                {errors.defaultQuantity && <span className={styles.error}>{errors.defaultQuantity}</span>}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>{la('duplicateCheckDaysLabel')}</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  className={`${styles.input} ${errors.duplicateCheckDays ? styles.inputError : ''}`}
                  value={formData.duplicateCheckDays}
                  onChange={(e) => handleChange('duplicateCheckDays', e.target.value)}
                />
                <span className={styles.hint}>{la('duplicateCheckDaysHint')}</span>
                {errors.duplicateCheckDays && <span className={styles.error}>{errors.duplicateCheckDays}</span>}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{la('startFromRow')}</label>
              <input
                type="number"
                min="1"
                className={`${styles.input} ${errors.lastRowIndex ? styles.inputError : ''}`}
                value={formData.lastRowIndex}
                onChange={(e) => handleChange('lastRowIndex', e.target.value)}
              />
              <span className={styles.hint}>{la('startFromRowHint')}</span>
              {errors.lastRowIndex && <span className={styles.error}>{errors.lastRowIndex}</span>}
            </div>

            <div className={styles.checkboxField}>
              <input
                type="checkbox"
                id="useCityUnknown"
                checked={formData.useCityUnknown}
                onChange={(e) => handleChange('useCityUnknown', e.target.checked)}
              />
              <label htmlFor="useCityUnknown">{la('useCityUnknown')}</label>
            </div>

            {mode === 'edit' && (
              <div className={styles.checkboxField}>
                <input
                  type="checkbox"
                  id="pauseForm"
                  checked={formData.status === 'paused'}
                  onChange={(e) => handleChange('status', e.target.checked ? 'paused' : 'active')}
                />
                <label htmlFor="pauseForm">{la('pauseForm')}</label>
              </div>
            )}
          </div>

          {/* Field mapping */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{la('fieldMapping')}</div>
            <p className={styles.sectionDesc}>{la('fieldMappingDescription')}</p>

            <div className={styles.loadColumnsRow}>
              {sheetColumns.length > 0 && (
                <span className={styles.columnsCount}>
                  {sheetColumns.length} {la('columnsLoaded')}
                </span>
              )}
              <button
                type="button"
                className={styles.loadColumnsBtn}
                onClick={handleLoadColumns}
                disabled={columnsLoading}
              >
                {columnsLoading ? (
                  <><Loader size={13} className={styles.spin} /> {la('loadingColumns')}</>
                ) : (
                  <><RefreshCw size={13} /> {la('loadColumns')}</>
                )}
              </button>
            </div>

            {columnsError && (
              <div className={styles.columnsError}>
                <AlertCircle size={14} />
                {columnsError}
              </div>
            )}

            {sheetColumns.length === 0 && !columnsError && (
              <p className={styles.noColumnsHint}>{la('enterSheetIdAndTabFirst')}</p>
            )}

            {CANONICAL_FIELDS.map((field) => (
              <div key={field} className={styles.mappingRow}>
                <span className={styles.canonicalLabel}>
                  {la(`canonicalFields.${field}`)}
                </span>
                <select
                  className={styles.mappingSelect}
                  value={getMappedColumn(field)}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                >
                  <option value="">{la('noMapping')}</option>
                  {sheetColumns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>
              {la('cancel')}
            </button>
            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting ? (
                <><Loader size={14} className={styles.spin} /> {la('saving')}</>
              ) : (
                la('save')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadFormModal;
